import { mergeRefs } from "@circle-react/helpers/refHelpers";
import { isFunction } from "lodash";
import { forwardRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { RegisterOptions } from "react-hook-form";

import { Input } from "@/react/components/SettingsApp/CompassCompat/Input";
import type {
  InputOrArea,
  InputProps,
} from "@/react/components/SettingsApp/CompassCompat/Input";
import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

export interface FormInputProps extends InputProps {
  name: string;
  rules?: RegisterOptions;
  transformValue?: (value: any) => any;
}

export const FormInput = forwardRef<InputOrArea, FormInputProps>(
  (
    {
      className: classNameProp,
      hideBorder = false,
      name,
      noPadding = false,
      onBlur,
      onChange,
      prefix,
      rules = {},
      suffix,
      transformValue,
      variant = "text",
      required = false,
      ...rest
    },
    ref,
  ) => {
    const className = classNameProp ?? anchorClassNames("form-control");
    const { register } = useFormContext();
    const isCompassNumber =
      useIsCircleV4FeatureEnabled("circle_v4_form_elements_enabled") &&
      rest.type === "number";
    const watchedValue = useWatch({ name });

    const {
      ref: formRef,
      onChange: formOnChange,
      onBlur: formOnBlur,
      name: formName,
    } = register(name, rules);

    const handleOnChange = (event: any) => {
      if (isFunction(transformValue)) {
        event.target.value = transformValue(event.target.value);
      }

      if (isFunction(onChange)) onChange(event);
      void formOnChange(event);
    };

    const handleOnBlur = (event: any) => {
      if (isFunction(onBlur)) onBlur(event);
      void formOnBlur(event);
    };

    const hasRequiredRule = Boolean(
      rules && typeof rules.required === "boolean" && rules.required,
    );

    return (
      <Input
        name={formName}
        className={className}
        hideBorder={hideBorder}
        noPadding={noPadding}
        prefix={prefix}
        suffix={suffix}
        variant={variant}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        ref={mergeRefs([ref, formRef])}
        required={required || hasRequiredRule}
        {...rest}
        {...(isCompassNumber && { value: watchedValue ?? null })}
      />
    );
  },
);

FormInput.displayName = "Form.Input";
